import mergeWith from 'lodash.mergewith';
import { use } from 'react';

import { ClubFinderContext } from 'src/content/club-finder-context';
import { getDefaultBuildTypeForCountry } from 'src/enums/build/build-type';
import type { Country } from 'src/enums/country';
import type { Language } from 'src/enums/language';
import { group } from 'src/helpers/group-helper';
import { getBestLanguageMatch } from 'src/helpers/language-helper';
import { getGlobalPagePath, getSiteUrlForPath } from 'src/helpers/navigation-helper';
import { trimAddressString, trimLocationString } from 'src/helpers/string-helper';

export const useClubFinderContext = () => {
  const clubFinderContext = use(ClubFinderContext);

  if (clubFinderContext === null || clubFinderContext === undefined) {
    throw new Error('useClubFinderContext must be called within a <ClubFinderContext.Provider />');
  }

  return clubFinderContext;
};

export type CmsClubDetails = {
  clubName: string;
  siteId: number;
  clubCoordinates: {
    lat: number;
    lng: number;
  };
  googlePlaceId?: string;
  clubThumbnailImage: string;
  clubAddress: string;
  clubAddressText1: string;
  clubAddressText2: string;
  googleMapsLink: string;
  isComingSoon: boolean;
};

export type ClubFinderClubNode = {
  readonly relativeDirectory: string;
  readonly childClubJson: Queries._ClubFinderClubDetailsFragment | null;
};

export const parseClubDetailsData = (
  nodes: readonly ClubFinderClubNode[],
  allowedDirectories: readonly string[]
): CmsClubDetails[] => {
  const allowedDirectorySet = new Set(allowedDirectories);
  const nodesByDirectory = group(nodes, (n) => n.relativeDirectory);
  const result: CmsClubDetails[] = [];

  for (const [directory, nodesInDirectory] of nodesByDirectory) {
    if (!allowedDirectorySet.has(directory)) continue;
    const merged = mergeClubFinderQueryNodeChildren(
      nodesInDirectory.map((node) => node.childClubJson!)
    );
    result.push({
      siteId: parseInt(merged.clubId!),
      clubName: merged.clubName,
      clubThumbnailImage: merged.clubThumbnailImage,
      clubAddress: trimLocationString(merged.clubLocation!.text1!, merged.clubLocation!.text2!),
      clubAddressText1: trimAddressString(merged.clubLocation!.text1),
      clubAddressText2: trimAddressString(merged.clubLocation!.text2),
      clubCoordinates: {
        lat: parseFloat(merged.clubLocation!.lat!),
        lng: parseFloat(merged.clubLocation!.lng!),
      },
      googlePlaceId: merged.clubLocation?.placeId,
      isComingSoon: merged.comingSoonClubFinder,
      googleMapsLink: merged.clubLocation!.url,
    } as unknown as CmsClubDetails);
  }
  return result;
};

const mergeClubFinderQueryNodeChildren = (
  nodes: Queries._ClubFinderClubDetailsFragment[]
): Queries._ClubFinderClubDetailsFragment => {
  return mergeWith({}, ...nodes, (value1: unknown, value2: unknown) =>
    value1 === undefined || value1 === null ? value2 : value1
  ) as Queries._ClubFinderClubDetailsFragment;
};

export const getLinkForCountry = (
  destCountry: Country,
  currentLanguage: Language,
  destPath = '/'
): string => {
  const destBuildType = getDefaultBuildTypeForCountry(destCountry);
  const bestDestLanguage = getBestLanguageMatch(currentLanguage, destBuildType);
  return getSiteUrlForPath(
    destBuildType,
    getGlobalPagePath(destPath, bestDestLanguage, destBuildType)
  );
};

export const createGoogleMapsUrl = (coordinates: { lat: number; lng: number }): string =>
  `https://maps.google.com/?q=${coordinates.lat},${coordinates.lng}`;
